import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    AfterViewInit,
    SimpleChanges,
} from '@angular/core';
import {
    RangeBegin,
    CALENDAR_SELECT,
    LocalBusinesses,
    EntryPassageData,
} from '@models';
import { CountingService } from '@app/services';
import { CountingCircleStateService } from '@app/services/counting-circle-state.service';
import { Subscription } from 'rxjs';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
    CURRENT_PALETTE,
    VERSUS_PALETTE,
    getColor,
} from './../../providers/entry-passage-colors';

type EntryPassageTab = 'visits' | 'groups';

@Component({
    standalone: false,
    selector: 'app-entry-passage',
    templateUrl: './entry-passage.component.html',
    styleUrls: ['./entry-passage.component.scss'],
})
export class EntryPassageComponent
    implements OnChanges, OnDestroy, AfterViewInit
{
    @Input() calendarSelect!: CALENDAR_SELECT;
    @Input() localBusiness!: LocalBusinesses;
    @Input() range!: RangeBegin;

    public data!: EntryPassageData['current'];
    public dataVs!: EntryPassageData['versus'];
    public comparison!: EntryPassageData['comparison'];

    public entryTab: EntryPassageTab = 'visits';

    public entriesKeys: string[] = [];
    public hourLabels: string[] = [];
    /** Libellés du versus, alignés par index sur hourLabels (week/month/year). */
    public versusLabels: string[] = [];
    public rowHeights: Record<string, number> = {};

    public loading = false;
    public emptyState = false;
    public isClosed = false;

    private dataSub?: Subscription;
    private charts: Chart[] = [];
    private viewInitialized = false;

    constructor(
        private countingService: CountingService,
        private circleState: CountingCircleStateService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (
            (changes['calendarSelect'] ||
                changes['localBusiness'] ||
                changes['range']) &&
            this.localBusiness &&
            this.calendarSelect &&
            this.range
        ) {
            this.circleState.setLoading();
            this.loadData();
        }
    }

    ngAfterViewInit(): void {
        this.viewInitialized = true;
        if (this.data && this.dataVs && !this.loading) {
            this.renderCharts();
        }
    }

    private loadData(): void {
        this.loading = true;
        this.dataSub?.unsubscribe();

        this.dataSub = this.countingService
            .getEntryPassageData(
                this.localBusiness.id!,
                this.calendarSelect,
                this.range
            )
            .subscribe({
                next: (result: EntryPassageData) => {
                    this.data = result.current;
                    this.dataVs = result.versus;
                    this.comparison = result.comparison;
                    this.isClosed = (result as any).closed === true;

                    if (!this.groupsCountingEnabled) {
                        this.entryTab = 'visits';
                    }

                    this.parseData();
                    this.loading = false;

                    this.emitCircleData();

                    setTimeout(() => this.renderCharts(), 0);
                },
                error: () => {
                    this.loading = false;
                    this.isClosed = false;
                    this.emptyState = true;
                },
            });
    }

    private parseData(): void {
        if (this.isClosed) {
            this.entriesKeys = [];
            this.hourLabels = [];
            this.versusLabels = [];
            this.rowHeights = {};
            this.emptyState = true;
            return;
        }

        const levelKey = this.getLevelKey();
        const byLevel = this.data?.[levelKey] ?? {};
        const byLevelVs = this.dataVs?.[levelKey] ?? {};

        this.entriesKeys = Object.keys(byLevel);
        this.emptyState = this.entriesKeys.length === 0;

        if (this.emptyState) {
            this.hourLabels = [];
            this.versusLabels = [];
            this.rowHeights = {};
            return;
        }

        // Créneaux horaires pris depuis la première entrée trouvée
        const firstEntry = this.entriesKeys[0];
        this.hourLabels = firstEntry
            ? Object.keys(byLevel[firstEntry] ?? byLevelVs[firstEntry] ?? {})
            : [];

        // En jour, le libellé (créneau horaire) est identique pour le courant et
        // le versus. En semaine/mois/année, les dates diffèrent : on récupère
        // les libellés du versus pour les afficher sous ceux du courant.
        const firstEntryVs = Object.keys(byLevelVs)[0];
        this.versusLabels =
            !this.isDayFormat && firstEntryVs
                ? Object.keys(byLevelVs[firstEntryVs] ?? {})
                : [];

        this.computeRowHeights();
    }

    /**
     * Les barres à 0 ne sont pas rendues : la hauteur de chaque créneau est
     * calculée sur le nombre de barres réellement visibles (courant + versus).
     */
    private computeRowHeights(): void {
        const levelKey = this.getLevelKey();
        const byLevel = this.data?.[levelKey] ?? {};
        const byLevelVs = this.dataVs?.[levelKey] ?? {};

        this.rowHeights = {};

        this.hourLabels.forEach((hourLabel, hourIndex) => {
            const versusLabel = this.getVersusKey(hourIndex, hourLabel);
            const barsCount = this.entriesKeys.reduce((count, entryName) => {
                const current = byLevel[entryName]?.[hourLabel] ?? 0;
                const versus = byLevelVs[entryName]?.[versusLabel] ?? 0;
                return count + (current > 0 ? 1 : 0) + (versus > 0 ? 1 : 0);
            }, 0);

            this.rowHeights[hourLabel] = this.getRowHeight(barsCount);
        });
    }

    /** Le site n'a pas forcément accès au comptage groupe : onglets masqués. */
    public get groupsCountingEnabled(): boolean {
        return this.localBusiness?.groupsCountingEnabled === true;
    }

    public get isDayFormat(): boolean {
        return this.calendarSelect === CALENDAR_SELECT.DAY;
    }

    public getVersusLabel(index: number): string {
        return this.versusLabels[index] ?? '';
    }

    /** Clé d'accès aux données versus : même libellé en jour, date décalée sinon. */
    private getVersusKey(index: number, hourLabel: string): string {
        return this.versusLabels[index] ?? hourLabel;
    }

    private getLevelKey(): 'nbVisitsByLevel' | 'nbGroupsByLevel' {
        return this.entryTab === 'visits'
            ? 'nbVisitsByLevel'
            : 'nbGroupsByLevel';
    }

    public setEntryTab(tab: EntryPassageTab): void {
        this.entryTab = tab;
        this.parseData();
        setTimeout(() => this.renderCharts(), 0);
        this.emitCircleData();
    }

    private emitCircleData(): void {
        if (this.isClosed) {
            this.circleState.emit({
                current: 0,
                versus: 0,
                variation: 0,
                icon:
                    this.entryTab === 'visits'
                        ? '/assets/svg/customers-dark.svg'
                        : '/assets/svg/customers-group-dark.svg',
            });
            return;
        }

        if (!this.data || !this.dataVs) return;

        const isVisits = this.entryTab === 'visits';

        this.circleState.emit({
            current: (isVisits ? this.data.nbPerson : this.data.nbGroups) ?? 0,
            versus:
                (isVisits ? this.dataVs.nbPerson : this.dataVs.nbGroups) ?? 0,
            variation: isVisits
                ? this.comparison?.nbPerson ?? 0
                : this.comparison?.nbGroups ?? 0,
            icon: isVisits
                ? '/assets/svg/customers-dark.svg'
                : '/assets/svg/customers-group-dark.svg',
        });
    }

    private renderCharts(): void {
        if (!this.viewInitialized) return;

        this.destroyCharts();
        if (this.emptyState || !this.hourLabels.length) return;

        const levelKey = this.getLevelKey();
        const byLevel = this.data[levelKey];
        const byLevelVs = this.dataVs[levelKey];

        this.hourLabels.forEach((hourLabel, hourIndex) => {
            const canvas = document.getElementById(
                'entry_chart' + hourIndex
            ) as HTMLCanvasElement;
            if (!canvas) return;

            const versusLabel = this.getVersusKey(hourIndex, hourLabel);
            const totalCurrent = this.getHourTotal(hourLabel, true);
            const totalVersus = this.getHourTotal(hourLabel, false, hourIndex);

            const currentDatasets = this.entriesKeys
                .map((entryName, i) => ({ entryName, i }))
                .filter(
                    ({ entryName }) => (byLevel[entryName]?.[hourLabel] ?? 0) > 0
                )
                .map(({ entryName, i }) => ({
                label: entryName,
                // La longueur des barres est exprimée en % du total du créneau :
                // sinon un 92% (petit total) paraît plus long qu'un 100%.
                data: [
                    this.toPercent(
                        byLevel[entryName]?.[hourLabel] ?? 0,
                        totalCurrent
                    ),
                ],
                backgroundColor: getColor(CURRENT_PALETTE, i),
                borderColor: getColor(CURRENT_PALETTE, i),

                barPercentage: 0.5,
                categoryPercentage: 0.8,

                borderRadius: { bottomRight: 25, topRight: 25 },

                datalabels: {
                    display: (ctx: any) => (ctx.dataset.data[0] ?? 0) > 0,
                    anchor: 'end' as const,
                    align: 'right' as const,
                    clamp: true,
                    clip: false,
                    color: getColor(CURRENT_PALETTE, i),
                    font: { size: 9, weight: 'bold' as const },
                    formatter: (value: number) => Math.round(value) + '%',
                },
            }));

            const versusDatasets = this.entriesKeys
                .map((entryName, i) => ({ entryName, i }))
                .filter(
                    ({ entryName }) =>
                        (byLevelVs[entryName]?.[versusLabel] ?? 0) > 0
                )
                .map(({ entryName, i }) => ({
                label: entryName + ' (vs)',
                data: [
                    this.toPercent(
                        byLevelVs[entryName]?.[versusLabel] ?? 0,
                        totalVersus
                    ),
                ],
                backgroundColor: getColor(VERSUS_PALETTE, i),
                borderColor: getColor(VERSUS_PALETTE, i),

                barPercentage: 0.6,
                categoryPercentage: 0.8,

                borderRadius: { bottomRight: 25, topRight: 25 },
                datalabels: {
                    display: (ctx: any) => (ctx.dataset.data[0] ?? 0) > 0,
                    anchor: 'end' as const,
                    align: 'right' as const,
                    clamp: true,
                    clip: false,
                    color: getColor(VERSUS_PALETTE, i),
                    font: { size: 9, weight: 'bold' as const },
                    formatter: (value: number) => Math.round(value) + '%',
                },
            }));

            this.charts.push(
                new Chart(canvas, {
                    type: 'bar',
                    plugins: [ChartDataLabels], // scope local, pas d'impact sur les autres charts de l'appli
                    data: {
                        labels: [hourLabel],
                        datasets: [...currentDatasets, ...versusDatasets],
                    },
                    options: {
                        indexAxis: 'y',
                        elements: { bar: { borderWidth: 1 } }, // 2 -> 1, libère un peu de place
                        responsive: true,
                        maintainAspectRatio: false,
                        layout: {
                            padding: { right: 40 }, // place pour le texte "100%" sans rognage
                        },
                        scales: {
                            y: {
                                stacked: false,
                                ticks: { display: false },
                            },
                            x: {
                                beginAtZero: true,
                                display: false,
                                // Échelle fixe 0-100 % : deux barres de même
                                // pourcentage ont la même longueur.
                                max: 100,
                            },
                        },
                        plugins: {
                            legend: { display: false },
                            title: { display: false },
                            tooltip: { enabled: false },
                        },
                    },
                })
            );
        });
    }

    /** Part d'une entrée dans le total du créneau, en %. */
    private toPercent(value: number, total: number): number {
        return total > 0 ? (value / total) * 100 : 0;
    }

    public getEntryColor(index: number): string {
        return getColor(CURRENT_PALETTE, index);
    }

    public getEntryVersusColor(index: number): string {
        return getColor(VERSUS_PALETTE, index);
    }

    private readonly BAR_HEIGHT = 10;
    private readonly BAR_GAP = 4;
    /** 2 lignes de totaux (courant + versus) à afficher même sans barre. */
    private readonly MIN_ROW_HEIGHT = 34;
    /** En week/month/year le libellé tient sur 2 lignes (courant + versus). */
    private readonly MIN_ROW_HEIGHT_COMPARE = 38;

    public getRowHeight(barsCount: number): number {
        const minHeight = this.isDayFormat
            ? this.MIN_ROW_HEIGHT
            : this.MIN_ROW_HEIGHT_COMPARE;

        if (barsCount <= 0) return minHeight;

        return Math.max(
            barsCount * this.BAR_HEIGHT + (barsCount - 1) * this.BAR_GAP + 10,
            minHeight
        );
    }

    public getHourTotal(
        hourLabel: string,
        isCurrent: boolean,
        index?: number
    ): number {
        const levelKey = this.getLevelKey();
        const source = isCurrent
            ? this.data?.[levelKey]
            : this.dataVs?.[levelKey];
        if (!source) return 0;

        const key =
            isCurrent || index === undefined
                ? hourLabel
                : this.getVersusKey(index, hourLabel);

        return this.entriesKeys.reduce(
            (sum, entryName) => sum + (source[entryName]?.[key] ?? 0),
            0
        );
    }

    private destroyCharts(): void {
        this.charts.forEach((chart) => chart.destroy());
        this.charts = [];
    }

    ngOnDestroy(): void {
        this.dataSub?.unsubscribe();
        this.destroyCharts();
    }
}
